import type { QueryClient } from '@tanstack/react-query';

import type { NotificationsStreamEvent } from '../apis/notifications.js';
import { coalescedInvalidation } from '../activities/hooks/coalescedInvalidation.js';
import { notificationsKeys } from './queryKeys.js';

/**
 * How long the first signal waits before the list is asked again. Long enough
 * for a message that lands in the conversation on screen to be marked read
 * first, so the bell or badge does not flash for something the person is
 * already looking at.
 */
export const NOTIFICATIONS_ASK_PAUSE_MS = 300;

/** While signals keep coming, the list is asked no more often than this. */
export const NOTIFICATIONS_ASK_SPACING_MS = 1_000;

export interface INotificationsAskAgain {
  /** A frame from the person's stream: asks again if it concerns the site showing. */
  onSignal(event: NotificationsStreamEvent): void;
  /** The stream (re)opened: asks again — a signal may have been missed while it was down. */
  onOpen(): void;
  /** Drops an ask still waiting. */
  dispose(): void;
}

/**
 * The "ask again" behind the web bell and the mobile badge: turns the signals
 * of the person's stream into refetches of the notifications list. Neither
 * client counts on its own — a signal only says "ask again".
 *
 * The stream is the person's, not a site's: the server signals everyone a
 * change concerns, on every device, whatever site each is showing, because it
 * cannot know which — a client switches site without reconnecting. So the
 * site check is made here, against `siteId()` as it is when the signal
 * arrives: a signal about another site changes nothing, and that site's list
 * is asked when it is shown.
 *
 * A burst of signals costs a few requests, not one per signal: a signal that
 * arrives while one is waiting rides along with it, a request is never sent
 * within a second of the last, and never while one is still in flight.
 */
export function notificationsAskAgain(queryClient: QueryClient, siteId: () => string | null): INotificationsAskAgain {
  const askAgain = coalescedInvalidation(queryClient, notificationsKeys.all());
  let waiting: ReturnType<typeof setTimeout> | null = null;
  let lastAskedAt = 0;

  const askSoon = () => {
    if (waiting) return;
    const wait = Math.max(NOTIFICATIONS_ASK_PAUSE_MS, lastAskedAt + NOTIFICATIONS_ASK_SPACING_MS - Date.now());
    waiting = setTimeout(() => {
      waiting = null;
      lastAskedAt = Date.now();
      askAgain();
    }, wait);
  };

  return {
    onSignal(event) {
      if (event.channel !== 'notifications.changed') return;
      if (event.data.siteId === undefined || event.data.siteId === siteId()) askSoon();
    },
    onOpen: askSoon,
    dispose() {
      if (waiting) clearTimeout(waiting);
      waiting = null;
    },
  };
}
